import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { withRedirectedFrom } from '@lib/auth/utils/with-redirected-from';
import { APP_ROUTES } from '@lib/constants/apis';

const SENTINEL_ORIGIN = 'https://redirect-validator.invalid';

export function isOrgInviteJoinRedirect(path: string): boolean {
  const url = new URL(path, SENTINEL_ORIGIN);
  if (url.pathname !== APP_ROUTES.JOIN) {
    return false;
  }
  const token = url.searchParams.get('token');
  return token !== null && token.length > 0;
}

export function resolvePostSignupRedirect(
  redirectedFrom: string | null | undefined
): string {
  const safe = sanitizeRedirectUrl(redirectedFrom);
  if (isOrgInviteJoinRedirect(safe)) {
    return safe;
  }
  return withRedirectedFrom(APP_ROUTES.ONBOARDING, safe);
}

/**
 * Single landing destination for `/onboarding` so join-invite
 * bounce and the onboarding-complete home redirect cannot race.
 * `null` means stay on onboarding.
 */
export function resolveOnboardingLandingRedirect({
  fromOrgInviteLink,
  redirectedFrom,
  onboardingComplete,
  phoneVerificationRequired = false,
}: {
  fromOrgInviteLink: boolean;
  redirectedFrom: string;
  onboardingComplete: boolean;
  phoneVerificationRequired?: boolean;
}): string | null {
  if (phoneVerificationRequired) {
    return null;
  }
  if (!fromOrgInviteLink && isOrgInviteJoinRedirect(redirectedFrom)) {
    return redirectedFrom;
  }
  if (onboardingComplete) {
    if (
      redirectedFrom &&
      redirectedFrom !== APP_ROUTES.HOME &&
      redirectedFrom !== APP_ROUTES.ONBOARDING &&
      !isOrgInviteJoinRedirect(redirectedFrom)
    ) {
      return redirectedFrom;
    }
    return APP_ROUTES.HOME;
  }
  return null;
}

/**
 * Where to navigate after the onboarding phone step completes or is
 * skipped. `null` means continue the new-user flow (welcome → questionnaire).
 */
export function resolvePostPhoneStepRedirect({
  isJoinPreClaimRedirect,
  redirectedFrom,
  fromOrgInviteLink,
  onboardingComplete,
}: {
  isJoinPreClaimRedirect: boolean;
  redirectedFrom: string;
  fromOrgInviteLink: boolean;
  onboardingComplete: boolean;
}): string | null {
  if (isJoinPreClaimRedirect) {
    return redirectedFrom;
  }
  if (onboardingComplete) {
    return resolveOnboardingLandingRedirect({
      fromOrgInviteLink,
      redirectedFrom,
      onboardingComplete: true,
      phoneVerificationRequired: false,
    });
  }
  return null;
}

/**
 * Post-questionnaire landing when onboarding is newly complete.
 * Never bounces to a leftover join URL — the invite was already
 * claimed (or never applied) before the questionnaire ran.
 */
export function resolveOnboardingCompleteRedirect(
  redirectedFrom: string | null | undefined
): string {
  const safe = sanitizeRedirectUrl(redirectedFrom);
  if (
    safe &&
    safe !== APP_ROUTES.HOME &&
    safe !== APP_ROUTES.ONBOARDING &&
    !isOrgInviteJoinRedirect(safe)
  ) {
    return safe;
  }
  return APP_ROUTES.HOME;
}
